import { memo } from 'react'

import { ASPECT, VIEW_H, VIEW_W, pctToView, type Pt } from '@/geo/types'

/**
 * A way in or out of the stage (§4.3.1 `Portal`).
 *
 * An **edge** portal is a bar along that side of the picture; an
 * **anchor** portal is a chevron sitting on the anchor it belongs to. An
 * unlinked portal is dashed — legal (it "leads somewhere" and the story
 * decides) but visibly provisional.
 */

export type PortalEdge = 'left' | 'right' | 'top' | 'bottom'

export interface PortalMarkerProps {
  id: string
  kind: string
  edge?: PortalEdge
  /** Position when the portal hangs off an anchor rather than an edge. */
  at?: Pt
  linked: boolean
  colour: string
  radius: number
  selected?: boolean
  hovered?: boolean
  ghost?: boolean
  label?: string
  onPointerDown?: (ev: React.PointerEvent<SVGElement>) => void
}

const BAND = 2.4

export const PortalMarker = memo(function PortalMarker({
  id,
  kind,
  edge,
  at,
  linked,
  colour,
  radius,
  selected,
  hovered,
  ghost,
  label,
  onPointerDown,
}: PortalMarkerProps) {
  const dash = linked ? undefined : `${BAND * 0.5} ${BAND * 0.4}`
  const common = {
    'data-id': id,
    fill: colour,
    fillOpacity: ghost ? 0.18 : selected ? 0.55 : hovered ? 0.42 : 0.3,
    stroke: colour,
    strokeWidth: selected ? 0.45 : 0.3,
    strokeDasharray: dash,
    vectorEffect: 'non-scaling-stroke' as const,
    style: { pointerEvents: ghost ? ('none' as const) : ('auto' as const), cursor: 'pointer' },
    onPointerDown,
  }

  const title = `${label ?? kind}${linked ? '' : ' — not linked to anything yet'}`

  if (edge) {
    const rect =
      edge === 'left'
        ? { x: 0, y: 0, width: BAND, height: VIEW_H }
        : edge === 'right'
          ? { x: VIEW_W - BAND, y: 0, width: BAND, height: VIEW_H }
          : edge === 'top'
            ? { x: 0, y: 0, width: VIEW_W, height: BAND * ASPECT * 2 }
            : { x: 0, y: VIEW_H - BAND * ASPECT * 2, width: VIEW_W, height: BAND * ASPECT * 2 }
    return (
      <g role="img" aria-label={title}>
        <rect {...rect} {...common} rx={0.4} />
        <title>{title}</title>
      </g>
    )
  }

  if (!at) return null
  const [x, y] = pctToView(at)
  const r = Math.max(0.5, radius)
  return (
    <g role="img" aria-label={title} {...{ 'data-id': id }}>
      <title>{title}</title>
      <path
        d={`M${x - r},${y - r} L${x + r * 0.4},${y} L${x - r},${y + r} Z`}
        {...common}
      />
    </g>
  )
})
